import React, {FC} from 'react';
import styled from 'styled-components';

import {PaletteColorsEnum} from '../../../../libs/shared/core/src/models/api/paletteModel';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {emojiToDefinition} from '../../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {HighlightTypesEnum} from '../../../models/api/highlightBaseModel';
import {TagHighlightEmojiModel, TagModel} from '../../../models/api/tagModel';
import {baseEmojiValidationMap} from '../../../services/emojis/emojis';
import {Emoji} from '../emoji/emoji';

/*
 * Props.
 */

interface TagPillProps {
  tag: TagModel;
  className?: string;
}

/*
 * Style.
 */

interface TagStyleProps {
  $color: PaletteColorsEnum;
}
const StyledTagDiv = styled.div<TagStyleProps>`
  padding: 1px 3px;
  border-radius: 2px;
  background-color: ${(p) => p.theme.palette[p.$color].shade10};
  border: 1px solid ${(p) => p.theme.palette[p.$color].shade30};

  color: ${(p) => p.theme.palette[p.$color].shade40};
  overflow: hidden;
`;

const StyledNameDiv = styled.div`
  line-height: 12px;
  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.semibold};
  text-transform: uppercase;

  transform: translateY(-0.5px);
`;

/*
 * Component.
 */

export const TagPill: FC<TagPillProps> = ({tag, className}) => {
  if (tag.type in HighlightTypesEnum) {
    return <div className={className}>{renderContent(tag)}</div>;
  }

  return null;
};

function renderContent(tag: TagModel) {
  switch (tag.type) {
    case HighlightTypesEnum.EMOJI:
      return renderEmoji(tag);
    case HighlightTypesEnum.COLOR:
      return renderColorTag(tag.name, tag.highlightColor);
    case HighlightTypesEnum.NONE:
      return renderColorTag(tag.name, PaletteColorsEnum.GREY);
    default:
      return null;
  }
}

function renderColorTag(name: string, color: PaletteColorsEnum) {
  return (
    <StyledTagDiv $color={color}>
      <StyledNameDiv>{name}</StyledNameDiv>
    </StyledTagDiv>
  );
}

function renderEmoji(tag: TagHighlightEmojiModel) {
  const definition = emojiToDefinition(tag.highlightEmoji, baseEmojiValidationMap);
  if (!definition) {
    return null;
  }

  return <Emoji {...definition} size={16} />;
}
